import { useState, useEffect } from "react";
import type React from "react";
import { useLocalization } from "@fluent/react";
import {
  Spinner,
  Button,
  Radio,
  Row,
  Chip,
} from "@narmi/design_system";
import { ContextForm, useNotificationContext } from "cerulean";
import Role, { useRolesContext } from "byzantine/src/Role";
import OrganizationUser from "byzantine/src/OrganizationUser";
import { useNavigate } from "react-router-dom";
import NewUserActions from "./NewUserActions";
import RoleDetailsDrawer from "./RoleDetailsDrawer";
import styles from "./NewUserFlow.module.scss";
import { NewUserFormData } from "./NewUserInfo";
import { DEFAULT_ROLE_NAMES } from "./OrganizationRoles.helpers";

interface NewUserRoleSelectionProps {
  formData: NewUserFormData;
  onSubmit: (data: NewUserFormData & { role_uuid: string }) => void;
  onCancel: () => void;
  isPending?: boolean;
  organizationUsers?: OrganizationUser[];
  allAccounts?: (API.Account | API.ExternalAccount)[];
  orgLimits?: API.Limits.ByType;
  hasInstitutionLoaded?: boolean;
}

export function NewUserRoleSelection({
  formData,
  onSubmit,
  onCancel,
  isPending = false,
  organizationUsers,
  allAccounts,
  orgLimits,
  hasInstitutionLoaded,
}: NewUserRoleSelectionProps) {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const {
    assignableRoles,
    isLoading: rolesLoading,
    error: rolesError,
  } = useRolesContext();
  const [selectedRoleUuid, setSelectedRoleUuid] = useState<string>("");
  const [selectedRoleForDetails, setSelectedRoleForDetails] =
    useState<Role | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    if (rolesError) {
      sendNotification({
        type: "negative",
        text: l10n.getString("new-user-error-loading-roles"),
      });
    }
  }, [rolesError, sendNotification, l10n]);

  const handleSubmit = (callback: () => void) => {
    if (!selectedRoleUuid.trim()) {
      sendNotification({
        type: "negative",
        text: l10n.getString("new-user-validation-role-required"),
      });
      callback();
      return;
    }

    onSubmit({
      ...formData,
      role_uuid: selectedRoleUuid,
    });
    callback();
  };

  const handleViewDetails = (e: React.MouseEvent, role: Role) => {
    e.stopPropagation();
    setSelectedRoleForDetails(role);
    setIsDrawerOpen(true);
  };

  const handleSelectRoleFromDrawer = (role: Role) => {
    setSelectedRoleUuid(role.uuid || "");
  };

  if (rolesLoading) {
    return (
      <div style={{ minHeight: "800px" }}>
        <div className={styles.flowContainer}>
          <div className="section-box">
            <div className={styles.loadingContainer}>
              <Spinner />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "800px" }}>
      <div className={styles.flowContainer}>
        <div className="section-box">
          <h2 className={styles.stepTitle}>
            {l10n.getString("new-user-role-title")}
          </h2>
          <p className={styles.stepSubtitle}>
            {l10n.getString("new-user-role-description")}{" "}
            <b>{`${formData.first_name} ${formData.last_name}`}</b>
            {". "}
            {l10n.getString("new-user-select-role-change-later")}
          </p>

          <ContextForm nativeForm={false}>
            <div className={styles.formContent}>
              <fieldset className={styles.roleSelection}>
                <Row justifyContent="start">
                  <Row.Item>
                    <div className={styles.roleSelectionLabel}>
                      {l10n.getString("new-user-select-role")}
                    </div>
                  </Row.Item>
                  <Row.Item shrink>
                    <div>
                      {assignableRoles.length}{" "}
                      {l10n.getString("new-user-count-roles")}
                    </div>
                  </Row.Item>
                </Row>

                <div className={styles.rolesScrollContainer}>
                  {assignableRoles.map((role) => (
                    <div key={role.uuid} style={{ minHeight: "120px", display: "flex" }}>
                      <Radio
                        key={role.uuid}
                        value={role.uuid || ""}
                        name="new-user-role"
                        kind="input-card"
                        checked={selectedRoleUuid === role.uuid}
                        onCheck={setSelectedRoleUuid}
                      >
                        <div style={{ flex: 1 }}>
                          <Row gapSize="xs">
                            <div className={styles.roleName}>{role.name}</div>
                            {DEFAULT_ROLE_NAMES.has(role.name) && (
                              <Chip
                                startIcon="lock"
                                label={l10n.getString(
                                  "organization-roles-default"
                                )}
                              ></Chip>
                            )}
                          </Row>
                          <p
                            style={{
                              marginBottom: "var(--space--xs)",
                              minHeight: "1.5em",
                            }}
                          >
                            {role.description}
                          </p>
                          <Button
                            kind="plain"
                            onClick={(e) => handleViewDetails(e, role)}
                          >
                            {l10n.getString("new-user-view-details")}
                          </Button>
                        </div>
                      </Radio>
                    </div>
                  ))}
                </div>
              </fieldset>
            </div>
            <Row justifyContent="center" alignItems="center" gapSize="xs">
              <p style={{ margin: 0 }}>{l10n.getString("new-user-cant-find-role")}</p>
              <Button
                kind="plain"
                endIcon={"arrow-right"}
                label={l10n.getString("new-user-create-new-role")}
                onClick={() =>
                  navigate("../roles/new")
                }
              ></Button>
            </Row>
            <NewUserActions
              onSubmit={handleSubmit}
              onCancel={onCancel}
              isDisabled={isPending}
              submitLabel="Send invite"
            />
          </ContextForm>
        </div>
      </div>

      <RoleDetailsDrawer
        role={selectedRoleForDetails}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSelectRole={handleSelectRoleFromDrawer}
        organizationUsers={organizationUsers}
        allAccounts={allAccounts}
        orgLimits={orgLimits}
        hasInstitutionLoaded={hasInstitutionLoaded}
      />
    </div>
  );
}

export default NewUserRoleSelection;
